import { Box, Card, Stack, Typography, Radio, Checkbox } from '@mui/joy';
import { CheckCircle, XCircle } from "@phosphor-icons/react";
import { SafeHTML } from '../../../utils';

/**
 * Вариант ответа. Перенесён из TestSessionPage без изменений — скину UWorld
 * он нужен таким же, отличаться будет только оформление вокруг.
 */
export default function AnswerOption({ answer, type, selected, onChange, showResult, userAnswer, isCorrect, letter, instantAnswerMode = false, instantAnswerResult = null, onInstantAnswerCheck, t = (en) => en }) {
    const isUserSelected = userAnswer?.selected_answers?.some(sa => sa.id === answer.id);
    const isCorrectAnswer = answer.is_correct;
    
    // Определяем, показывать ли мгновенный результат
    const showInstantResult = instantAnswerMode && instantAnswerResult !== null;
    const isInstantSelected = showInstantResult && instantAnswerResult.selectedAnswerId === answer.id;
    const isInstantCorrect = showInstantResult && instantAnswerResult.correctAnswerId === answer.id;

    // Определяем стили в зависимости от состояния
    let variant = 'outlined';
    let color = 'neutral';
    let resultSx = {};

    if (showResult || showInstantResult) {
        if (isCorrectAnswer) {
            // Правильный ответ — vivid teal border + noticeable tint
            resultSx = {
                border: '1.5px solid',
                borderColor: (theme) => theme.palette.mode === 'dark' ? '#2dd4a8' : '#0F7B6C',
                bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(45, 212, 168, 0.14)' : 'rgba(15, 123, 108, 0.08)',
            };
        } else if (isUserSelected || isInstantSelected) {
            // Выбранный неправильный — refined crimson border + subtle tint
            resultSx = {
                border: '1.5px solid',
                borderColor: (theme) => theme.palette.mode === 'dark' ? '#e07070' : '#a83232',
                bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(190, 70, 70, 0.14)' : 'rgba(168, 50, 50, 0.06)',
            };
        } else {
            // Остальные — приглушены
            resultSx = { opacity: 0.4 };
        }
    } else if (selected) {
        // Выбранный ответ (до отправки)
        variant = 'soft';
        color = 'primary';
    }

    const handleClick = () => {
        if (showResult || instantAnswerMode) {
            // В режиме показа результата или мгновенного ответа - не обрабатываем клик
            return;
        }
        
        if (onChange) {
            onChange();
        }
        
        // Если включен режим мгновенного ответа и это одиночный выбор
        if (instantAnswerMode && type === 'radio') {
            onInstantAnswerCheck?.(answer.id);
        }
    };

    return (
        <Card 
            variant={variant}
            color={color}
            component="label"
            sx={{
                p: 2,
                cursor: (showResult || showInstantResult) ? 'default' : 'pointer',
                transition: 'all 0.25s ease',
                ...resultSx,
                '&:hover': !showResult && !showInstantResult ? {
                    backgroundColor: 'background.level1',
                    borderColor: 'primary.300'
                } : {},
            }}
            onClick={handleClick}
        >
            <Stack direction="row" spacing={2} alignItems="center">
                {/* Буква варианта ответа */}
                {type === 'radio' ? (
                    <Radio 
                        value={answer.id}
                        checked={selected}
                        disabled={showResult || showInstantResult}
                        sx={{ mt: 0.25 }}
                    />
                ) : (
                    <Checkbox 
                        checked={selected}
                        onChange={onChange}
                        disabled={showResult || showInstantResult}
                        sx={{ mt: 0.25 }}
                    />
                )}
                
                <Box sx={{ flex: 1 }} onClick={handleClick}>
                    <Stack direction={'row'} spacing={1} alignItems="center">
                        <Box sx={{ 
                            borderRadius: 'sm', 
                            // backgroundColor: selected ? 'primary.500' : 'neutral.200',
                            color: selected ? showResult && isCorrectAnswer ? (theme) => theme.palette.mode === 'dark' ? '#2dd4a8' : '#0F7B6C' : '#DA7756' : 'text.primary',
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'center',
                            fontSize: 'lg',
                            fontWeight: 'bold',
                            flexShrink: 0
                        }}>
                            {letter}.
                        </Box>
                        
                        <Typography level="body1" sx={{ fontSize: { xs: '0.95rem', sm: '1.05rem' } }}>
                            {t(answer.text, answer.text_ru)}
                        </Typography>
                    </Stack>
                    
                    {/* Описание ответа (показываем только для правильных ответов после отправки) */}
                    {showResult && isCorrectAnswer && answer.description && (
                        <SafeHTML 
                            html={answer.description}
                            sx={{ 
                                mt: 1,
                                fontSize: 'sm',
                                color: 'text.secondary',
                                '& p': { mb: 0.5 }
                            }}
                        />
                    )}
                </Box>

                {/* Иконки статуса в режиме показа результата */}
                {(showResult || showInstantResult) && (
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {isCorrectAnswer ? (
                            <CheckCircle size={22} weight="fill" style={{ color: 'var(--joy-palette-mode) === "dark" ? "#2dd4a8" : "#0F7B6C"' }} color="#2dd4a8" />
                        ) : (isUserSelected || isInstantSelected) ? (
                            <XCircle size={20} weight="fill" color="#e07070" />
                        ) : (
                            <XCircle size={18} weight="regular" color="var(--joy-palette-neutral-500, #71717a)" />
                        )}
                    </Box>
                )}
            </Stack>
        </Card>
    );
}
